import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FeedbackType } from 'src/api/feedback';

import { startAppListening } from '../listenerMiddleware';

import {
  emptyFeedbacks,
  feedbackFirstPageNumber,
  initialState,
} from './initialState';
import { feedbackPageSize, fetchFeedbacks } from './services';
import { FeedbackPageMode } from './types';

export const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    setFeedbackType: (state, action: PayloadAction<FeedbackType>) => {
      state.feedbackType = action.payload;
    },
    setMode: (state, action: PayloadAction<FeedbackPageMode | undefined>) => {
      state.mode = action.payload;
    },
    setFeedback: (state, action: PayloadAction<number>) => {
      state.currentFeedbackId = action.payload;
      const currentFeedback = state.rows.find(
        ({ id }) => id === action.payload,
      );
      state.comment = currentFeedback?.completedComment ?? '';
    },
    setComment: (state, action: PayloadAction<string>) => {
      state.comment = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchFeedbacks.pending, (state, action) => {
      if (action.meta.arg) {
        state.rows = emptyFeedbacks;
        state.hasMore = true;
        state.pageNumber = feedbackFirstPageNumber;
      }
      state.error = undefined;
      state.loading = true;
      state.currentRequestId = action.meta.requestId;
    });
    builder.addCase(fetchFeedbacks.rejected, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.error = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchFeedbacks.fulfilled, (state, action) => {
      if (!action.payload || state.currentRequestId !== action.meta.requestId)
        return;

      const items = action.payload.pageItems;

      state.loading = false;
      state.hasMore = items.length >= feedbackPageSize;
      state.pageNumber++;
      state.rows = action.meta.arg ? items : [...state.rows, ...items];
    });
  },
});

export const feedbackCreated = createAction('feedback/feedbackCreated');

export const { actions: feedbackActions, reducer: feedbackReducer } =
  feedbackSlice;

startAppListening({
  predicate: (action, current, previous) => {
    return (
      current.feedback.feedbackType !== previous.feedback.feedbackType ||
      current.feedback.mode !== previous.feedback.mode
    );
  },
  effect: (action, listenerApi) => {
    listenerApi.dispatch(fetchFeedbacks(true));
  },
});

startAppListening({
  actionCreator: feedbackCreated,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(fetchFeedbacks(true));
  },
});
